import { useCallback, useRef, useState } from 'react';
import { getExportColumns, runAsyncExport } from '@/services/list-export.service';
import type { ExportStatusResponse } from '@/services/list-export.service';
import { ExportColumnsModal } from '@/components/export/ExportColumnsModal';
import { toast } from '@/store/toastStore';
import { getErrorMessage } from '@/services/apiClient';

interface UseAsyncExportOptions {
  entityType: string;
  entityLabel?: string;
}

/**
 * Hook that wraps the async export flow with a column-selection modal and
 * toast-based progress feedback.
 *
 * Usage:
 *   const { exporting, startExport, exportModal } = useAsyncExport({ entityType: 'patients' });
 *   <Button loading={exporting} onClick={() => startExport(filters)}>Export</Button>
 *   {exportModal}
 */
export function useAsyncExport({ entityType, entityLabel }: UseAsyncExportOptions) {
  const [exporting, setExporting] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [columns, setColumns] = useState<string[]>([]);
  const [columnsLoading, setColumnsLoading] = useState(false);
  const pendingFiltersRef = useRef<Record<string, unknown>>({});
  const toastIdRef = useRef<string | null>(null);

  const label = entityLabel ?? entityType.replace(/-/g, ' ');

  // Open the column-selection modal and load the column list.
  const startExport = useCallback(
    async (filters: Record<string, unknown> = {}) => {
      if (exporting) return;
      pendingFiltersRef.current = filters;
      setModalOpen(true);
      setColumnsLoading(true);
      try {
        const cols = await getExportColumns(entityType);
        setColumns(cols);
      } catch (err) {
        toast.error('Export failed', getErrorMessage(err, 'Unable to load export columns.'));
        setModalOpen(false);
      } finally {
        setColumnsLoading(false);
      }
    },
    [entityType, exporting],
  );

  const runExport = useCallback(
    async (selectedColumns: string[]) => {
      setModalOpen(false);
      setExporting(true);

      // Persistent (duration: 0) so the progress toast stays put for the whole
      // export instead of self-dismissing after 5s and being re-added on every
      // poll — that dismiss/re-add on each 2.5s poll was the toast that visibly
      // "loaded over and over". We create each phase toast exactly once and
      // dismiss it explicitly on the terminal outcome.
      toastIdRef.current = toast.custom({
        variant: 'info',
        title: 'Preparing export',
        description: `Your ${label} export is being prepared...`,
        duration: 0,
      });
      let showedProcessing = false;

      try {
        const rowCount = await runAsyncExport(
          entityType,
          pendingFiltersRef.current,
          (status: ExportStatusResponse) => {
            if (status.status === 'processing' && !showedProcessing) {
              showedProcessing = true;
              if (toastIdRef.current) toast.dismiss(toastIdRef.current);
              toastIdRef.current = toast.custom({
                variant: 'info',
                title: 'Generating export',
                description: `Building your ${label} spreadsheet...`,
                duration: 0,
              });
            }
          },
          selectedColumns,
        );

        if (toastIdRef.current) toast.dismiss(toastIdRef.current);
        toast.success('Export complete', `Exported ${rowCount.toLocaleString()} ${label}.`);
      } catch (err) {
        if (toastIdRef.current) toast.dismiss(toastIdRef.current);
        // The server's own explanation (who can fix a refusal) rather than
        // axios's "Request failed with status code 422".
        toast.error('Export failed', getErrorMessage(err, 'Unable to export.'));
      } finally {
        setExporting(false);
        toastIdRef.current = null;
      }
    },
    [entityType, label],
  );

  const exportModal = (
    <ExportColumnsModal
      isOpen={modalOpen}
      columns={columns}
      loading={columnsLoading}
      onConfirm={runExport}
      onClose={() => setModalOpen(false)}
    />
  );

  return { exporting, startExport, exportModal };
}
